import { createRouter as _createRouter, createMemoryHistory, createWebHistory } from 'vue-router';

import UserService from '@/services/UserService';
import StoreLayout from '@/layouts/StoreLayout.vue';
import InformationLayout from '@/layouts/InformationLayout.vue';
import Chronicler from '@/components/commons/Chronicler.js';
import LogoLayout from '@/layouts/LogoLayout.vue';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage';

function authGuard(to, from, next) {
  if (UserService.isVerified() && UserService.getCustomerToken() != null) {
    const { query } = to;

    if (query.next) {
      window.location.href = query.next;
    } else {
      next('/account');
    }
  } else {
    next();
  }
}

// eslint-disable-next-line import/prefer-default-export
export function createRouter() {
  const router = _createRouter({
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(),
    routes: [
      {
        name: 'home',
        path: '/',
        component: () => import('@/pages/Contentful/HomePage.vue'),
      },
      {
        name: 'contentfulPreview',
        path: '/contentful-preview/:entryId',
        component: () => import('@/pages/Contentful/ContentfulPreview.vue'),
      },
      {
        name: 'product',
        path: '/product/:id/:slug',
        component: () => import('@/pages/Product/ProductPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'pla',
        path: '/pla/:id/:slug',
        props: true,
        component: () => import('@/pages/Product/PLAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'ctfBrowse',
        path: '/browse/:slug',
        component: () => import('@/pages/Contentful/ContentfulBrowsePage.vue'),
        meta: { pageIdentifier: 'Browse' },
      },
      {
        name: 'browseDefault',
        path: '/browse',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Default Browse Page',
          critical: true,
        },
      },
      {
        name: 'browse',
        path: '/browse/:id/:slug',
        component: () => import('@/pages/BrowsePageSwitcher/BrowsePageSwitcher.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'room',
        path: '/room/:id/:slug',
        component: () => import('@/pages/Room/RoomPageNA.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'search',
        path: '/search',
        component: () => import('@/pages/Search/SearchNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'searchStatic',
        path: '/search/:slug.html',
        component: () => import('@/pages/Search/SearchNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'campaignPage',
        path: '/c/:slug',
        component: () => import('@/pages/Contentful/CampaignPage.vue'),
        meta: { pageIdentifier: 'Campaign' },
      },
      {
        name: 'landing',
        path: '/l/:slug',
        component: () => import('@/pages/Landing/LandingNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'collection',
        path: '/collection/:id/:slug',
        component: () => import('@/pages/Collection/CollectionNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'sale',
        path: '/sale',
        component: () => import('@/pages/Sale/SaleNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'clearance',
        path: '/clearance',
        component: () => import('@/pages/Clearance/ClearanceNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'financing',
        path: '/financing',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Financing',
          fallbackComponent: () => import('@/pages/Financing/FinancingPage.vue'),
          critical: true,
        },
      },
      {
        name: 'currentPromotion',
        path: '/promotion',
        component: () => import('@/pages/Promotion/PromotionNAPage.vue'),
      },
      {
        name: 'promotion',
        path: '/promotion/:id/:slug',
        component: () => import('@/pages/Promotion/PromotionNAPage.vue'),
      },
      // {
      //   name: 'tag',
      //   path: '/tag/:id/:slug',
      //   component: () => import('@/pages/Tag/TagPage.vue'),
      //   meta: {
      //     newsletter: true,
      //   },
      // },
      {
        name: 'shop',
        path: '/shop',
        component: () => import('@/pages/Shop/ShopNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'shopBrowse',
        path: '/shop/:slug',
        component: () => import('@/pages/Shop/ShopBrowsePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'cart',
        path: '/cart',
        component: () => import('@/pages/Cart/CartNAPage.vue'),
        meta: {
          title: 'Checkout',
        },
      },
      {
        name: 'cartAbandonment',
        path: '/cart/:sessionId',
        component: () => import('@/pages/Cart/CartAbandonmentPageNA.vue'),
        meta: {
          title: 'Checkout',
        },
      },
      {
        name: 'cartQuoteAbandonment',
        path: '/cart/:quoteId/:sessionId',
        component: () => import('@/pages/Cart/CartAbandonmentPageNA.vue'),
        meta: {
          title: 'Checkout',
        },
      },
      {
        name: 'affirmRedirect',
        path: '/api/checkout/affirmredirect/:paymentId',
        component: () => import('@/pages/Cart/AffirmRedirectPage.vue'),
        meta: {
          title: 'Redirecting to Affirm',
        },
      },
      {
        name: 'affirmRedirectNA',
        path: '/checkout/affirmredirect',
        component: () => import('@/pages/Cart/AffirmRedirectNAPage.vue'),
        meta: {
          title: 'Redirecting to Affirm',
        },
      },
      {
        name: 'affirmRedirectTMP',
        path: '/checkout/affirmredirect/:paymentId/:checkoutHashKey',
        component: () => import('@/pages/Cart/AffirmRedirectNAPage.vue'),
        meta: {
          title: 'Redirecting to Affirm',
        },
      },
      {
        name: 'affirmComplete',
        path: '/checkout/affirm/return',
        component: () => import('@/pages/Cart/AffirmCompletePage.vue'),
        meta: {
          title: 'Completing Affirm Purchase',
        },
      },
      {
        name: 'affirmCancel',
        path: '/checkout/affirm/cancel',
        component: () => import('@/pages/Cart/AffirmCancelPage.vue'),
        meta: {
          title: 'Canceling Affirm Purchase',
        },
      },
      {
        name: 'paybrightRedirect',
        path: '/api/checkout/paybrightredirect/:paymentId',
        component: () => import('@/pages/Cart/PayBrightRedirectPage.vue'),
        meta: {
          title: 'Redirecting to PayBright',
        },
      },
      {
        name: 'paybrightRedirectNA',
        path: '/checkout/paybrightredirect',
        component: () => import('@/pages/Cart/PayBrightRedirectNAPage.vue'),
        meta: {
          title: 'Redirecting to PayBright',
        },
      },
      {
        name: 'paybrightRedirectTMP',
        path: '/checkout/paybrightredirect/:paymentId/:checkoutHashKey',
        component: () => import('@/pages/Cart/PayBrightRedirectNAPage.vue'),
        meta: {
          title: 'Redirecting to PayBright',
        },
      },
      {
        name: 'paybrightComplete',
        path: '/checkout/payBright/complete',
        component: () => import('@/pages/Cart/PayBrightCompletePage.vue'),
        meta: {
          title: 'Completing PayBright Purchase',
        },
      },
      {
        name: 'paybrightCancel',
        path: '/checkout/payBright/cancel',
        component: () => import('@/pages/Cart/PayBrightCancelPage.vue'),
        meta: {
          title: 'Canceling PayBright Purchase',
        },
      },
      {
        name: 'resolveRedirect',
        path: '/api/checkout/resolvepayredirect/:paymentId',
        component: () => import('@/pages/Cart/ResolveRedirectPage.vue'),
        meta: {
          title: 'Redirecting to Resolve',
        },
      },
      {
        name: 'resolveRedirectNA',
        path: '/checkout/resolvepayredirect/:paymentId',
        component: () => import('@/pages/Cart/ResolvePayRedirectNAPage.vue'),
        meta: {
          title: 'Redirecting to Resolve',
        },
      },
      {
        name: 'lookbooks',
        path: '/lookbooks',
        component: () => import('@/pages/Lookbooks/LookbooksNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'lookbook',
        path: '/lookbook/:id/:slug',
        component: () => import('@/pages/Lookbooks/LookbookNAPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        name: 'login',
        path: '/login',
        component: () => import('@/pages/Auth/LoginPage.vue'),
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
      },
      {
        name: 'loginIncomplete',
        path: '/login/incomplete',
        component: () => import('@/pages/Auth/LoginIncompletePage.vue'),
        meta: { title: 'Complete Account Set-Up' },
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
        props: true,
      },
      {
        name: 'loginFromEmail',
        path: '/login/email',
        component: () => import('@/pages/Auth/LoginFromEmailPage.vue'),
      },
      {
        name: 'forgot',
        path: '/forgot',
        component: () => import('@/pages/Auth/ForgotPage.vue'),
        meta: { title: 'Forgot Your Password?' },
      },
      {
        name: 'forgotSuccess',
        path: '/forgot/success',
        component: () => import('@/pages/Auth/ForgotSentPage.vue'),
        meta: { title: 'Forgot Password Instructions' },
      },
      {
        name: 'signup',
        path: '/signup',
        component: () => import('@/pages/Auth/RegisterPage.vue'),
        meta: { title: 'Sign Up' },
        beforeEnter(to, from, next) {
          authGuard(to, from, next);
        },
      },
      {
        name: 'completeRequired',
        path: '/complete/required',
        component: () => import('@/pages/Auth/CompleteRequiredPage.vue'),
        meta: { title: 'Complete Your Account' },
      },
      {
        name: 'socialOnly',
        path: '/complete/socialOnly',
        component: () => import('@/pages/Auth/SocialOnlyPage.vue'),
        meta: { title: "You don't have a password" },
      },
      {
        name: 'verify',
        path: '/verify/:cypher/:code',
        component: () => import('@/pages/Auth/VerifyPage.vue'),
      },
      {
        name: 'verifyRequired',
        path: '/verify/required',
        component: () => import('@/pages/Auth/VerifyRequiredPage.vue'),
        meta: { title: 'Verify Your Account' },
      },
      {
        name: 'verifySuccess',
        path: '/verify/success',
        component: () => import('@/pages/Auth/VerifySuccessPage.vue'),
        meta: { title: 'Account Verified' },
        beforeEnter(to, from, next) {
          if (!to.query.already_verified) {
            if (to.query.__i) {
              UserService.setUser({
                sessionId: to.query.__i,
              });
            } else {
              UserService.setUser(null);
            }
            if (to.query.__t) {
              UserService.setCustomerToken(to.query.__t);
            } else {
              UserService.setCustomerToken(null);
            }
          }
          next();
        },
      },
      {
        name: 'verifyError',
        path: '/verify/error',
        component: () => import('@/pages/Auth/VerifyErrorPage.vue'),
        meta: { title: 'Verification Error' },
      },
      {
        name: 'resetRequired',
        path: '/reset/required',
        component: () => import('@/pages/Auth/ResetRequiredPage.vue'),
        meta: { title: 'Update Your Password' },
      },
      {
        name: 'reset',
        path: '/reset/:cypher/:code',
        component: () => import('@/pages/Auth/ResetPasswordPage.vue'),
        meta: { title: 'Update Your Password' },
      },
      {
        name: 'confirmation',
        path: '/checkout/confirm',
        component: () => import('@/pages/Confirm/ConfirmationPage.vue'),
        meta: { title: 'Order Confirmed' },
      },
      {
        name: 'confirmationTMP',
        path: '/checkout/confirm/:cypher',
        component: () => import('@/pages/Confirm/ConfirmationPage.vue'),
        meta: { title: 'Order Confirmed' },
      },
      {
        name: 'confirmationPurchase',
        path: '/checkout/confirm/purchase',
        component: () => import('@/pages/Confirm/ConfirmationPage.vue'),
        meta: { title: 'Order Confirmed' },
      },
      {
        name: 'confirmationPurchaseTMP',
        path: '/checkout/confirm/purchase/:cypher',
        component: () => import('@/pages/Confirm/ConfirmationPage.vue'),
        meta: { title: 'Order Confirmed' },
      },
      {
        path: '/account',
        name: 'account',
        alias: '/menu',
        component: () => import('@/components/account/AccountPage.vue'),
        beforeEnter(to, from, next) {
          if (!import.meta.env.SSR && (!UserService.isVerified() || UserService.getCustomerToken() == null)) {
            window.location.href = `/login?next=${to.fullPath}`;
          } else {
            next();
          }
        },
        children: [
          {
            name: 'myaccount',
            path: 'myaccount',
            redirect: {
              name: 'menu',
            },
          },
          {
            name: 'menu',
            path: '',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountOrders.vue'),
            },
          },
          {
            name: 'orders',
            path: 'orders',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountOrders.vue'),
            },
          },
          {
            path: 'personal',
            name: 'personal',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountPersonal.vue'),
            },
          },
          {
            path: 'storeCredits',
            name: 'storeCredits',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountCredits.vue'),
            },
          },
          {
            path: 'changepassword',
            name: 'changepassword',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountPassword.vue'),
            },
          },
          {
            path: 'communication',
            name: 'communication',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountCommunication.vue'),
            },
          },
          {
            path: 'reviews',
            name: 'accountReviews',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountReviews.vue'),
            },
          },
          {
            path: 'reviews/:productId',
            name: 'accountProductReview',
            components: {
              navigation: () => import('@/components/account/AccountReviewNavigation.vue'),
              content: () => import('@/components/account/AccountProductReview.vue'),
            },
          },
          {
            path: 'supportRequests',
            name: 'supportRequests',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountSupport.vue'),
            },
          },
          {
            path: 'supportRequest/:claimId',
            name: 'supportRequest',
            components: {
              navigation: () => import('@/components/account/AccountSupportNavigation.vue'),
              content: () => import('@/components/account/AccountSupportRequest.vue'),
            },
            props: {
              content: true,
            },
          },
          {
            path: 'supportRequest',
            name: 'supportRequestFallback',
            components: {
              navigation: () => import('@/components/account/AccountNavigation.vue'),
              content: () => import('@/components/account/AccountSupport.vue'),
            },
            beforeEnter: (to, from, next) => {
              if (!import.meta.env.SSR) {
                if (to.query && to.query.claimId) {
                  next({
                    name: 'supportRequest',
                    params: {
                      claimId: to.query.claimId,
                    },
                    replace: true,
                  });
                  return;
                }

                next('/404');
                return;
              }

              next();
            },
          },
          {
            path: 'orders/:orderId/track',
            name: 'track',
            components: {
              navigation: () => import('@/components/account/AccountTrackNavigation.vue'),
              content: () => import('@/components/account/AccountTrack.vue'),
            },
          },
          {
            path: 'trackOrders',
            redirect: {
              path: '/account',
            },
          },
        ],
      },
      {
        path: '/favorites',
        name: 'favorites',
        component: () => import('@/pages/Favorite/FavoritePage.vue'),
        meta: {
          title: 'Favorites',
        },
      },
      {
        path: '/favorites/:uuid',
        name: 'favoritesshare',
        component: () => import('@/pages/Favorite/FavoritePage.vue'),
        meta: {
          title: 'Favorites',
        },
      },
      {
        path: '/delivery',
        name: 'delivery',
        redirect: '/help-center/delivery',
        redirectParams: {
          responseCode: 301,
        },
      },
      {
        path: '/delivery/:slug',
        name: 'deliveryPreview',
        redirect: '/help-center/delivery',
        redirectParams: {
          responseCode: 301,
        },
      },
      {
        path: '/returns',
        name: 'returns',
        redirect: '/help-center/returns',
        redirectParams: {
          responseCode: 301,
        },
      },
      {
        path: '/returns/:slug',
        name: 'returnsPreview',
        redirect: '/help-center/returns',
        redirectParams: {
          responseCode: 301,
        },
      },
      {
        path: '/aboutus',
        name: 'aboutus',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'About Us',
          fallbackComponent: () => import('@/pages/Static/AboutusPage.vue'),
        },
      },
      {
        path: '/order/:token/verify',
        name: 'orderVerify',
        component: () => import('@/pages/Order/OrderVerifyPage.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Order Verification',
        },
      },
      {
        path: '/reviews/edit',
        component: () => import('@/pages/EditReviews/EditReviewsLayout.vue'),
        children: [
          {
            path: '',
            name: 'editReviews',
            component: () => import('@/pages/EditReviews/EditReviewsPage.vue'),
            meta: {
              layout: LogoLayout,
              title: 'Your Reviews',
            },
          },
          {
            path: 'order',
            name: 'editOrderReviews',
            component: () => import('@/pages/EditReviews/EditOrderReviewsPage.vue'),
            meta: {
              layout: LogoLayout,
              title: 'Review Your Experience',
            },
          },
          {
            path: 'product',
            name: 'editProductReviews',
            component: () => import('@/pages/EditReviews/EditProductReviewsPage.vue'),
            meta: {
              layout: LogoLayout,
              title: 'Review Your Product',
            },
          },
        ],
      },
      {
        path: '/giftcards/:token/verify',
        name: 'giftCardVerify',
        component: () => import('@/pages/Giftcards/GiftCardVerifyPage.vue'),
        meta: {
          layout: InformationLayout,
          title: 'Gift Card Verification',
        },
      },
      {
        path: '/giftcards',
        name: 'giftcards',
        component: () => import('@/pages/Giftcards/GiftCardPage.vue'),
        children: [
          {
            path: 'buy',
            name: 'giftCardBuy',
            component: () => import('@/pages/Giftcards/GiftCardBuy.vue'),
          },
          {
            path: 'confirm/:token',
            name: 'giftCardConfirmation',
            component: () => import('@/pages/Giftcards/GiftCardConfirmation.vue'),
            meta: {
              title: 'Gift Card Purchase',
            },
          },
          {
            path: 'terms',
            name: 'giftCardTerms',
            component: () => import('@/pages/Giftcards/GiftCardTerms.vue'),
            meta: {
              layout: InformationLayout,
              title: 'Gift Card',
            },
          },
          {
            path: 'activate/:token',
            name: 'giftCardActivate',
            component: () => import('@/pages/Giftcards/GiftCardActivate.vue'),
            meta: {
              layout: InformationLayout,
              title: 'Gift Card',
            },
          },
        ],
      },
      {
        path: '/press',
        name: 'press',
        component: () => import('@/pages/Static/PressPage.vue'),
        meta: {
          fullTitle: 'In the Press | Article',
          fullDescription:
            "We're proud of our press and media content. Read about Article in the news and visit our media kit for brand assets and press resources.",
          newsletter: true,
        },
      },
      {
        path: '/contact',
        name: 'contact',
        component: () => import('@/pages/Contact/ContactPage.vue'),
      },
      {
        path: '/contact/message',
        name: 'message',
        component: () => import('@/pages/Contact/ContactMessagePage.vue'),
        beforeEnter: (to) => {
          if (!import.meta.env.SSR && to.query?.type === 'swatches') {
            window.location.href = '/design-resources/free-swatches';
            return false;
          }
        },
      },
      {
        path: '/contact/data',
        name: 'data',
        component: () => import('@/pages/Contact/MyDataPage.vue'),
        meta: {
          title: 'My Data',
        },
      },
      {
        path: '/sitemap',
        name: 'sitemap',
        component: () => import('@/pages/Sitemap/SiteMapNAPage.vue'),
      },
      {
        path: '/tou',
        name: 'termsOfUse',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Terms of Use',
          fallbackComponent: () => import('@/pages/Static/TermsOfUsePage.vue'),
        },
      },
      {
        path: '/reviews',
        name: 'customerReviews',
        component: () => import('@/pages/Static/ReviewsPage.vue'),
        meta: {
          fullTitle: 'Furniture Reviews | Article',
          fullDescription:
            "Don't just take our word for it. Read more than 33,500 reviews from happy and stylish Article customers. See for yourself: browse now.",
          newsletter: true,
        },
      },
      {
        path: '/privacy',
        name: 'privacyPolicy',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Privacy Policy',
          fallbackComponent: () => import('@/pages/Static/PrivacyPage.vue'),
        },
      },
      {
        path: '/covid-19',
        name: 'COVID19Information',
        component: () => import('@/pages/Static/COVID19Information.vue'),
      },
      {
        path: '/covid-19/:slug',
        name: 'COVID19InformationPreview',
        component: () => import('@/pages/Static/COVID19Information.vue'),
      },
      {
        path: '/faq',
        name: 'faq',
        component: () => import('@/pages/Static/FaqPage.vue'),
        meta: {
          acceptableQueries: ['q'],
          newsletter: true,
        },
      },
      {
        path: '/value',
        name: 'value',
        component: () => import('@/pages/Static/ValuePage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/accessibility',
        name: 'accessibility',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Accessibility',
          fallbackComponent: () => import('@/pages/Static/AccessibilityPage.vue'),
        },
      },
      {
        path: '/trade',
        name: 'tradeSignUp',
        component: () => import('@/pages/Trade/TradePage.vue'),
        meta: {
          title: 'Article Trade Program',
        },
      },
      {
        path: '/trade-favorites',
        name: 'tradeFavorites',
        component: () => import('@/pages/Contentful/ContentfulActivePage.vue'),
        meta: {
          pageIdentifier: 'Trade Favorites',
        },
      },
      {
        path: '/business',
        name: 'businessSignUp',
        component: () => import('@/pages/Trade/BusinessPage.vue'),
        meta: {
          title: 'Article Business Program',
        },
      },
      {
        name: 'businessUrlDiscount',
        path: '/discount/business/:discount',
        component: () => import('@/pages/Trade/BusinessPage.vue'),
      },
      {
        path: '/star',
        name: 'starTrade',
        redirect: {
          name: 'tradeSignUp',
        },
      },
      {
        path: '/furniture-bundles',
        name: 'sets',
        component: () => import('@/pages/Sets/SetsPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/furniture-bundles/:id/:slug',
        name: 'set',
        component: () => import('@/pages/Sets/SetPage.vue'),
        meta: {
          newsletter: true,
        },
      },
      {
        path: '/ourarticle',
        name: 'ugcDisplay',
        component: () => import('@/pages/UGC/UGCDisplayPage.vue'),
        meta: {
          title: 'Article Social Club',
          newsletter: true,
        },
      },

      {
        path: '/sofa-finder',
        name: 'productFinder',
        component: () => import('@/pages/ProductFinder/ProductFinderLayout.vue'),
        meta: {
          title: 'Sofa Finder',
        },
      },

      {
        path: '/help-center',
        component: () => import('@/pages/HelpCenter/HelpCenterLayout.vue'),
        children: [
          {
            path: '',
            name: 'helpCenter',
            component: () => import('@/pages/HelpCenter/HelpCenterPage.vue'),
          },
          {
            path: 'search',
            name: 'helpCenterSearch',
            component: () => import('@/pages/HelpCenter/HelpCenterSearchPage.vue'),
          },
          {
            path: ':page',
            name: 'helpCenterTopic',
            component: () => import('@/pages/HelpCenter/HelpCenterTopic.vue'),
            meta: {
              helpCenterScrollBehavior: true,
            },
          },
          {
            path: ':page/:tag',
            name: 'helpCenterTag',
            component: () => import('@/pages/HelpCenter/HelpCenterTopic.vue'),
          },
        ],
      },

      {
        path: '/500',
        name: 'serverError',
        component: () => import('@/pages/Errors/ServerErrorPage.vue'),
        meta: {
          title: 'Internal Server Error',
        },
      },
      {
        path: '/404',
        name: 'notFound',
        component: () => import('@/pages/Errors/NotFoundPage.vue'),
      },
      {
        name: 'homeUrlDiscountLegacy', // LEGACY - kill with old homepage
        path: '/:discount',
        component: () => import('@/pages/Contentful/HomePage.vue'),
      },
      {
        name: 'homeUrlDiscount',
        path: '/discount/:discount',
        component: () => import('@/pages/Contentful/HomePage.vue'),
      },
      {
        name: 'tradeUrlDiscount',
        path: '/discount/trade/:discount',
        component: () => import('@/pages/Trade/TradePage.vue'),
      },
      {
        name: 'homePreviewLegacy',
        path: '/home/:id/:slug',
        component: () => import('@/pages/Contentful/HomePage.vue'),
      },
      {
        name: 'homePreview', // LEGACY - kill with old homepage
        path: '/home/:slug',
        component: () => import('@/pages/Contentful/HomePage.vue'),
      },
      {
        path: '/:pathMatch(.*)*',
        alias: '/404',
        name: 'notFoundAll',
        component: () => import('@/pages/Errors/NotFoundPage.vue'),
      },
      {
        path: '/:pathMatch(.*)*',
        alias: '/500',
        name: 'serverErrorAll',
        component: () => import('@/pages/Errors/ServerErrorPage.vue'),
        meta: {
          title: 'Internal Server Error',
        },
      },
      {
        path: '/design-resources/free-swatches',
        name: 'swatchesSearch',
        component: () => import('@/pages/Swatches/SwatchesLandingPage.vue'),
      },
      {
        path: '/design-resources/swatches-result',
        name: 'swatchesResult',
        component: () => import('@/pages/Swatches/SwatchesResultPage.vue'),
      },
      {
        path: '/design-resources/swatches-select/:id/:slug',
        name: 'swatchesSelect',
        component: () => import('@/pages/Swatches/SwatchesSelectPage.vue'),
      },
      {
        path: '/design-resources/swatches-review',
        name: 'SwatchesReview',
        component: () => import('@/pages/Swatches/SwatchesReviewPage.vue'),
      },
      {
        path: '/design-resources/swatches-confirmation',
        name: 'swatchesConfirmation',
        component: () => import('@/pages/Swatches/SwatchesConfirmationPage.vue'),
      },
    ],
    scrollBehavior(to, from, savedPosition) {
      if (to.hash) {
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            resolve({
              el: to.hash,
              behavior: 'smooth',
            });
          }, 500);
        });
      } else if (savedPosition) {
        return new Promise((resolve, reject) => {
          window.addEventListener('load', () => {
            setTimeout(() => {
              resolve(savedPosition);
            }, 500);
          });
        });
      } else if (to.params.saveScrollPosition) {
        return {};
      } else {
        return { left: 0, top: 0 };
      }
    },
  });

  router.beforeEach((to, from, next) => {
    if (to.meta && !to.meta.layout) {
      to.meta.layout = StoreLayout;
    }

    Chronicler.track('Page View');
    LocalStorage.set(LocalStorageKeys.ON_VISIT_TIME, JSON.stringify(Date.now()));

    next();
  });

  return router;
}
